import React, { useEffect, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import client from '../../api/client';
import { Hotel, Sparkles, Plus, Trash2, Save, Loader2, ExternalLink, MapPin, Car, Bus } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

export default function Hotels() {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  // Multi-select price categories. Empty = "variety" fallback. Each
  // selected category asks the AI for ~3 real hotels in that band.
  const [priceLevels, setPriceLevels] = useState(['mid']);
  const [rideshare, setRideshare] = useState({ providers: [], shuttleNote: '', shuttleUrl: '' });
  const [rsSaving, setRsSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const [h, r] = await Promise.all([
          client.get('/hotels'),
          client.get('/rideshare'),
        ]);
        setHotels(h.data.hotels || []);
        setRideshare({
          providers: r.data.providers || [],
          shuttleNote: r.data.shuttleNote || '',
          shuttleUrl: r.data.shuttleUrl || '',
        });
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      const payload = { hotels: next.map(({ id, ...rest }) => rest) };
      const { data } = await client.put('/hotels', payload);
      setHotels(data.hotels || []);
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const addBlank = () => {
    const next = [...hotels, { id: `tmp-${Date.now()}`, name: '', url: '', address: '', priceRange: '', distance: '', notes: '' }];
    setHotels(next);
  };
  const updateAt = (i, patch) => {
    const next = hotels.map((h, idx) => (idx === i ? { ...h, ...patch } : h));
    setHotels(next);
  };
  const removeAt = async (i) => {
    const next = hotels.filter((_, idx) => idx !== i);
    setHotels(next);
    await persist(next);
  };
  const saveAll = () => persist(hotels);

  const saveRideshare = async (next) => {
    setRsSaving(true);
    try {
      const { data } = await client.put('/rideshare', next);
      setRideshare({ providers: data.providers || [], shuttleNote: data.shuttleNote || '', shuttleUrl: data.shuttleUrl || '' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message });
    } finally {
      setRsSaving(false);
    }
  };
  const addProvider = (preset = { name: '', code: '', url: '', note: '' }) => {
    const next = { ...rideshare, providers: [...(rideshare.providers || []), preset] };
    setRideshare(next);
  };
  const updateProvider = (i, patch) => {
    const next = { ...rideshare, providers: rideshare.providers.map((p, idx) => idx === i ? { ...p, ...patch } : p) };
    setRideshare(next);
  };
  const removeProvider = async (i) => {
    const next = { ...rideshare, providers: rideshare.providers.filter((_, idx) => idx !== i) };
    setRideshare(next);
    await saveRideshare(next);
  };

  const askAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await client.post('/hotels/ai-suggest', {
        priceLevels: priceLevels.length ? priceLevels : undefined,
        count: 3,
      });
      setHotels(data.hotels || []);
      toast({ title: `Added ${data.added} hotel${data.added === 1 ? '' : 's'}`, description: 'Review, edit, or remove — we merged with your list.' });
    } catch (e) {
      toast({ title: 'AI unavailable', description: e?.response?.data?.detail || e.message });
    } finally {
      setAiLoading(false);
    }
  };

  // Toggle a price category on/off; empty selection is allowed (falls back
  // to a "variety" mix on the backend).
  const togglePriceLevel = (lvl) => {
    setPriceLevels((cur) => (cur.includes(lvl) ? cur.filter((x) => x !== lvl) : [...cur, lvl]));
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#7B2C3B]" /></div>;

  return (
    <div data-testid="hotels-page" className="animate-float-up max-w-4xl">
      <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <p className="section-eyebrow">Out-of-town guests</p>
          <h1 className="font-serif text-4xl text-[#2A2A2A] mt-2 flex items-center gap-3"><AppIcon feature="hotels" size="lg" reveal testId="section-icon-hotels" />
            Hotels
          </h1>
          <p className="text-[#6A6A6A] mt-1 text-sm">Curate a short list of nearby hotels. Appears in your wedding site&apos;s Travel section AND in invitations sent to guests marked <span className="text-[#7B2C3B] font-medium">Out of Towner</span>.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Multi-select price bands — 3 real hotels are pulled per ticked
              category, giving couples a wider selection than a single band. */}
          <div data-testid="hotels-price-levels" className="inline-flex items-center gap-1 rounded-full bg-[#FBF7F2] border border-[#EFE3D8] p-1 text-[11px]">
            {[
              { id: 'budget',  label: 'Budget'  },
              { id: 'mid',     label: 'Mid-range' },
              { id: 'upscale', label: 'Upscale' },
              { id: 'luxury',  label: 'Luxury'  },
            ].map((opt) => {
              const on = priceLevels.includes(opt.id);
              return (
                <button
                  type="button"
                  key={opt.id}
                  data-testid={`hotels-price-${opt.id}`}
                  onClick={() => togglePriceLevel(opt.id)}
                  className={`px-2.5 py-1 rounded-full transition-all ${on ? 'bg-[#7B2C3B] text-white' : 'text-[#3A3A3A] hover:bg-[#F4E4DA]'}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <span className="text-[10px] text-[#9A8A7E]">
            {priceLevels.length > 0
              ? `AI suggests 3 per band · ~${priceLevels.length * 3} total`
              : 'Pick 1 or more · AI suggests a variety mix'}
          </span>
          <button
            data-testid="hotels-ai-suggest"
            onClick={askAi}
            disabled={aiLoading}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] disabled:opacity-60"
          >
            {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Ask AI to suggest
          </button>
          <button
            data-testid="hotels-add"
            onClick={addBlank}
            className="btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
      </div>

      {hotels.length === 0 && (
        <div className="rounded-2xl p-8 border border-dashed border-[#B8896C] bg-white text-center">
          <Hotel className="w-10 h-10 text-[#B8896C] mx-auto mb-3" />
          <h3 className="font-serif text-xl text-[#2A2A2A]">No hotels yet</h3>
          <p className="text-sm text-[#6A6A6A] mt-1 max-w-md mx-auto">Add nearby hotels yourself, or let AI suggest 4 based on your venue. All show up on your wedding site &amp; in OOT-guest invitations.</p>
        </div>
      )}

      <div className="space-y-3">
        {hotels.map((h, i) => (
          <div key={h.id} data-testid={`hotel-${i}`} className="soft-card rounded-2xl p-4 bg-white border border-[#EFE3D8]">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-4">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Name</label>
                <input data-testid={`hotel-name-${i}`} value={h.name} onChange={(e) => updateAt(i, { name: e.target.value })} placeholder="Napa Valley Marriott" className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm" />
              </div>
              <div className="md:col-span-5">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Website</label>
                <input data-testid={`hotel-url-${i}`} value={h.url || ''} onChange={(e) => updateAt(i, { url: e.target.value })} placeholder="https://…" className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm" />
              </div>
              <div className="md:col-span-3">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Distance</label>
                <input data-testid={`hotel-distance-${i}`} value={h.distance || ''} onChange={(e) => updateAt(i, { distance: e.target.value })} placeholder="~4 mi from venue" className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm" />
              </div>
              <div className="md:col-span-4">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Price range</label>
                <input data-testid={`hotel-price-${i}`} value={h.priceRange || ''} onChange={(e) => updateAt(i, { priceRange: e.target.value })} placeholder="$250-$400/night" className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm" />
              </div>
              <div className="md:col-span-8">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Notes</label>
                <input data-testid={`hotel-notes-${i}`} value={h.notes || ''} onChange={(e) => updateAt(i, { notes: e.target.value })} placeholder="Quiet, walkable to town square" className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm" />
              </div>
              <div className="md:col-span-12">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Address</label>
                <input data-testid={`hotel-address-${i}`} value={h.address || ''} onChange={(e) => updateAt(i, { address: e.target.value })} placeholder="3425 Solano Ave, Napa CA" className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm" />
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-[#9A8A7E]">
                <MapPin className="w-3.5 h-3.5" />
                {h.address ? h.address : 'No address yet'}
              </div>
              <div className="flex items-center gap-2">
                {h.url && (
                  <a data-testid={`hotel-open-${i}`} href={h.url} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 text-[#7B2C3B] hover:underline">
                    Open <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <button data-testid={`hotel-remove-${i}`} onClick={() => removeAt(i)} className="text-[#B8896C] hover:text-red-600" title="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {hotels.length > 0 && (
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            data-testid="hotels-save-all"
            onClick={saveAll}
            disabled={saving}
            className="btn-primary inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save all
          </button>
        </div>
      )}

      {/* ---------- Ride share ---------- */}
      <section data-testid="rideshare-section" className="mt-12">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="section-eyebrow">Getting around</p>
            <h2 className="font-serif text-3xl text-[#2A2A2A] mt-1 flex items-center gap-2">
              <Car className="w-6 h-6 text-[#7B2C3B]" /> Ride share &amp; shuttles
            </h2>
            <p className="text-[#6A6A6A] text-sm mt-1">
              Add Uber / Lyft promo codes or a group shuttle — appears on your wedding site AND in emails to your out-of-town guests.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="rideshare-add-uber"
              onClick={() => addProvider({ name: 'Uber', code: '', url: 'https://www.uber.com/', note: '' })}
              className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2]"
            >
              + Uber
            </button>
            <button
              data-testid="rideshare-add-lyft"
              onClick={() => addProvider({ name: 'Lyft', code: '', url: 'https://www.lyft.com/', note: '' })}
              className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2]"
            >
              + Lyft
            </button>
            <button
              data-testid="rideshare-add-custom"
              onClick={() => addProvider()}
              className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] text-[#3A3A3A]"
            >
              + Other
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {(rideshare.providers || []).map((p, i) => (
            <div key={i} data-testid={`rideshare-provider-${i}`} className="soft-card rounded-2xl p-4 bg-white border border-[#EFE3D8]">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-3">
                  <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Provider</label>
                  <input data-testid={`rideshare-name-${i}`} value={p.name} onChange={(e) => updateProvider(i, { name: e.target.value })} placeholder="Uber, Lyft, Waymo…" className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm" />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Promo code</label>
                  <input data-testid={`rideshare-code-${i}`} value={p.code || ''} onChange={(e) => updateProvider(i, { code: e.target.value })} placeholder="WEDDING24" className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm font-mono uppercase" />
                </div>
                <div className="md:col-span-6">
                  <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Link</label>
                  <input data-testid={`rideshare-url-${i}`} value={p.url || ''} onChange={(e) => updateProvider(i, { url: e.target.value })} placeholder="https://…" className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm" />
                </div>
                <div className="md:col-span-12">
                  <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Note (optional)</label>
                  <input data-testid={`rideshare-note-${i}`} value={p.note || ''} onChange={(e) => updateProvider(i, { note: e.target.value })} placeholder="First-time riders get $20 off" className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm" />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end">
                <button data-testid={`rideshare-remove-${i}`} onClick={() => removeProvider(i)} className="text-[#B8896C] hover:text-red-600" title="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {/* Group shuttle */}
          <div className="soft-card rounded-2xl p-4 bg-white border border-[#EFE3D8]">
            <div className="flex items-center gap-2 mb-3">
              <Bus className="w-4 h-4 text-[#7B2C3B]" />
              <span className="font-serif text-lg text-[#2A2A2A]">Group shuttle (optional)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-8">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Shuttle note</label>
                <input data-testid="rideshare-shuttle-note" value={rideshare.shuttleNote || ''} onChange={(e) => setRideshare((r) => ({ ...r, shuttleNote: e.target.value }))} placeholder="Free shuttle from The Marriott to the venue at 4:30 PM sharp" className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm" />
              </div>
              <div className="md:col-span-4">
                <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Sign-up link (optional)</label>
                <input data-testid="rideshare-shuttle-url" value={rideshare.shuttleUrl || ''} onChange={(e) => setRideshare((r) => ({ ...r, shuttleUrl: e.target.value }))} placeholder="https://…" className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] bg-white text-sm" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end">
          <button
            data-testid="rideshare-save"
            onClick={() => saveRideshare(rideshare)}
            disabled={rsSaving}
            className="btn-primary inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full disabled:opacity-60"
          >
            {rsSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save ride share
          </button>
        </div>
      </section>
    </div>
  );
}
